import { useNavigate, useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LayoutGrid, PlusCircle, MessageCircle, User } from "lucide-react";
import { motion } from "framer-motion";

export default function BottomNavigation({ user, eventId, onAddClick, className, showAlways = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isPublicPage = location.pathname.startsWith("/e/");
  const isAuthPage = ["/login", "/auth"].some(path => location.pathname.startsWith(path));
  const urlEventId = location.pathname.match(/\/(?:event|e)\/((?!new)[^/]+)/)?.[1];
  const effectiveEventId = eventId || urlEventId;

  const handleNav = (path) => {
    navigate(path);
    // Force a small delay and scroll to top to ensure React Router processes it
    window.scrollTo(0, 0);
  };

  // Never show on auth pages
  if (isAuthPage) return null;

  // Show if logged in, or on public event pages, or if explicitly requested
  if (!user && !isPublicPage && !showAlways) return null;

  // Filter items for guests
  const allNavItems = [
    {
      key: "home",
      label: "Home",
      icon: LayoutGrid,
      to: "/dashboard",
      active: location.pathname === "/dashboard",
    },
    {
      key: "add",
      label: "Neu",
      icon: PlusCircle,
      to: "/event/new",
      onClick: onAddClick,
      active: location.pathname === "/event/new",
    },
    {
      key: "chat",
      label: "Chat",
      icon: MessageCircle,
      to: effectiveEventId ? `/event/${effectiveEventId}/chat` : "/dashboard",
      active: location.pathname.includes("/chat"),
    },
    {
      key: "account",
      label: "Profil",
      icon: User,
      to: "/profile",
      active: location.pathname === "/profile",
    },
  ];

  const navItems = user ? allNavItems : allNavItems.filter(item => ["home", "chat"].includes(item.key));

  return (
    <nav className={cn(
      "fixed bottom-0 left-0 right-0 z-50 bg-[#121212]/80 backdrop-blur-3xl border-t border-white/5 px-6 py-6 flex justify-between items-center rounded-t-[3rem] shadow-[0_-20px_50px_rgba(0,0,0,0.5)]",
      className
    )}
      style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 24px)" }}
    >
      <div className="flex items-center justify-between w-full max-w-md mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const content = (
            <>
              <Icon
                className={cn(
                  "w-7 h-7 transition-all group-hover:scale-110",
                  item.active ? "stroke-[2.5px] drop-shadow-[0_0_12px_var(--brand-accent)]" : "stroke-[2px]"
                )}
              />
              <span className={cn(
                "text-[10px] font-black uppercase tracking-[0.2em] leading-none",
                item.active ? "opacity-100" : "opacity-70"
              )}>
                {item.label}
              </span>
              {item.active && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -top-1 w-12 h-[2.5px] bg-brand-accent rounded-full shadow-[0_0_20px_var(--brand-accent)]"
                />
              )}
            </>
          );

          const commonClasses = cn(
            "flex flex-col items-center justify-center gap-2 transition-all group p-2 relative",
            item.active ? "text-brand-accent" : "text-white/60 hover:text-white"
          );

          if (item.onClick) {
            return (
              <button
                key={item.key}
                onClick={item.onClick}
                className={commonClasses}
              >
                {content}
              </button>
            );
          }

          return (
            <Link
              key={item.key}
              to={item.to}
              className={commonClasses}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
